import React, { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
	Anchor,
	Box,
	Button,
	Group,
	Loader,
	Paper,
	Stack,
	Tabs,
	Text,
	TextInput,
	Title,
	UnstyledButton,
	createStyles,
} from '@mantine/core'
import { Search } from 'tabler-icons-react'
import useOrgSearch from '../hooks/useOrgSearch'
import OrgSearchResultContent from '../components/OrgSearchResultContent'
import {
	ORG_SEARCH_CATEGORIES,
	ORG_SEARCH_MAX_LENGTH,
	ORG_SEARCH_MIN_LENGTH,
	orgSearchParams,
	orgSearchPath,
	orgSearchResultPath,
} from '../services/utils/orgSearch'

const useStyles = createStyles((theme) => ({
	result: {
		display: 'block',
		width: '100%',
		padding: '12px 16px',
		borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`,
		'&:last-child': { borderBottom: 0 },
		'&:hover, &:focus-visible': {
			background:
				theme.colorScheme === 'dark'
					? theme.colors.dark[5]
					: theme.colors.gray[0],
		},
	},
}))

export default function OrgSearchPage() {
	const { orgSlug } = useParams()
	const { classes } = useStyles()
	const [params, setParams] = useSearchParams()
	const query = (params.get('q') || '').trim()
	const requestedType = params.get('type') || 'all'
	const type = ORG_SEARCH_CATEGORIES.some(
		(category) => category.value === requestedType,
	)
		? requestedType
		: 'all'
	const requestedPage = Number(params.get('page') || 1)
	const page =
		type !== 'all' &&
		Number.isInteger(requestedPage) &&
		requestedPage >= 1 &&
		requestedPage <= 1000
			? requestedPage
			: 1
	const [draft, setDraft] = useState(query)
	const { data, error, loading, retry } = useOrgSearch(orgSlug, query, {
		type,
		page,
		limit: type === 'all' ? 5 : 25,
		delay: 0,
	})
	const groups = data?.groups || []
	const hasResults = groups.some((group) => group.items.length)
	useEffect(() => {
		setDraft(query)
	}, [query])
	const updateSearch = (term, category = 'all', nextPage = 1) => {
		setParams(orgSearchParams(term, category, nextPage))
	}

	return (
		<Box px='md' py='md' maw={1000}>
			<Title order={2} mb='xs'>
				Organization Search
			</Title>
			<Text color='dimmed' size='sm' mb='lg'>
				Find check-ins, experiences, and users in this organization.
			</Text>
			<form
				role='search'
				aria-label='Organization results search'
				onSubmit={(event) => {
					event.preventDefault()
					updateSearch(draft.trim())
				}}
			>
				<Group align='flex-end' noWrap>
					<TextInput
						label='Search organization'
						placeholder='Name, email, booking, or experience…'
						value={draft}
						onChange={(event) => setDraft(event.target.value)}
						maxLength={ORG_SEARCH_MAX_LENGTH}
						icon={<Search size={16} />}
						style={{ flex: 1 }}
					/>
					<Button
						type='submit'
						disabled={
							draft.trim().length < ORG_SEARCH_MIN_LENGTH ||
							draft.trim().length > ORG_SEARCH_MAX_LENGTH
						}
					>
						Search
					</Button>
				</Group>
			</form>
			<Tabs
				mt='lg'
				mb='lg'
				value={type}
				onTabChange={(category) => updateSearch(query, category)}
			>
				<Tabs.List>
					<Tabs.Tab value='all'>All results</Tabs.Tab>
					{ORG_SEARCH_CATEGORIES.map((category) => (
						<Tabs.Tab key={category.value} value={category.value}>
							{category.label}
						</Tabs.Tab>
					))}
				</Tabs.List>
			</Tabs>
			{/* {query.length < ORG_SEARCH_MIN_LENGTH && <Text color='dimmed'>Enter at least 2 characters to search.</Text>} */}
			{query.length > ORG_SEARCH_MAX_LENGTH && (
				<Text color='red' role='alert'>
					Use 200 characters or fewer.
				</Text>
			)}
			{loading && (
				<Group role='status'>
					<Loader size='sm' />
					<Text size='sm'>Searching…</Text>
				</Group>
			)}
			{error && (
				<Group>
					<Text color='red' role='alert'>
						{error}
					</Text>
					<Button variant='light' onClick={retry}>
						Retry
					</Button>
				</Group>
			)}
			{data && !hasResults && (
				<Text color='dimmed'>No results found for “{query}”.</Text>
			)}
			<Stack spacing='xl'>
				{groups
					.filter((group) => group.items.length)
					.map((group) => (
						<section
							key={group.type}
							aria-label={
								ORG_SEARCH_CATEGORIES.find(
									(category) => category.value === group.type,
								)?.label
							}
						>
							<Group position='apart' mb='sm'>
								<Title order={4}>
									{
										ORG_SEARCH_CATEGORIES.find(
											(category) => category.value === group.type,
										)?.label
									}
								</Title>
								{type === 'all' && group.hasMore && (
									<Anchor
										component={Link}
										size='sm'
										to={orgSearchPath(orgSlug, query, group.type)}
									>
										View all
									</Anchor>
								)}
							</Group>
							<Paper withBorder radius='md' style={{ overflow: 'hidden' }}>
								{group.items.map((item) => (
									<UnstyledButton
										component={Link}
										key={item.id}
										to={orgSearchResultPath(orgSlug, item)}
										className={classes.result}
									>
										<OrgSearchResultContent item={item} />
									</UnstyledButton>
								))}
							</Paper>
						</section>
					))}
			</Stack>
			{type !== 'all' && data && (page > 1 || groups[0]?.hasMore) && (
				<Group position='apart' mt='lg'>
					<Button
						variant='default'
						disabled={page === 1}
						onClick={() => updateSearch(query, type, page - 1)}
					>
						Previous
					</Button>
					<Text size='sm' color='dimmed'>
						Page {page}
					</Text>
					<Button
						variant='default'
						disabled={!groups[0]?.hasMore || page === 1000}
						onClick={() => updateSearch(query, type, page + 1)}
					>
						Next
					</Button>
				</Group>
			)}
		</Box>
	)
}
